/**
 * Operational (OP) domain types for payment sources.
 * These are shared across AP, AR, and other modules that need payment sources.
 */

import type {
  BankAccountStatus,
  BankAccountType,
  CreditCardBrand,
  CreditCardStatus,
  OnflyTravelAccountKey,
  PaymentCapabilityStatus,
  PaymentChannelCapabilityStatus,
  PaymentInstructionKind,
  ResourceAccessMode,
} from './generated/enums.js';

// ============================================================================
// Bank Account Types
// ============================================================================

/** Fine-grained access level for a protected bank account. */
export const BankAccountAccessLevel = {
  BALANCES: 'BALANCES',
  MOVEMENTS: 'MOVEMENTS',
} as const;
export type BankAccountAccessLevel =
  (typeof BankAccountAccessLevel)[keyof typeof BankAccountAccessLevel];

export interface BankAccountAccessMember {
  organizationMembershipId: string;
  userId: string;
  name: string;
  email: string;
  roleSlugs: string[];
  hasAccess: boolean;
  hasDirectAccess: boolean;
  groupNames: string[];
  accessLevel: BankAccountAccessLevel | null;
}

export interface BankAccountAccessGroup {
  groupId: string;
  name: string;
  description: string | null;
  hasAccess: boolean;
  accessLevel: BankAccountAccessLevel | null;
}

/** Public API contract for managing one bank account's visibility policy. */
export interface BankAccountAccessResponse {
  accountId: string;
  accessMode: ResourceAccessMode;
  members: BankAccountAccessMember[];
  groups: BankAccountAccessGroup[];
}

/**
 * Request payload for creating a new bank account
 */
export interface CreateBankAccountRequest {
  entityId: string;
  name: string;
  description?: string | null;
  /** Account holder name (nome do titular). Optional. */
  ownerName?: string | null;
  /** Bank code (e.g., 341 for Itaú). Optional for BENEFITS accounts. */
  bankCode?: string;
  /** Bank/provider name (e.g., "Itaú", "Caju", "Flash") */
  bankName: string;
  /** Institution logo URL from Open Finance (Pluggy connector.imageUrl). Optional. */
  bankIconUrl?: string | null;
  /** Branch code. Optional for BENEFITS accounts. */
  branchCode?: string;
  /** Account number. Optional for BENEFITS accounts. */
  accountNumber?: string;
  accountType: BankAccountType;
  /**
   * The account's own currency (ALC-1070/1071 decision #34). Required explicitly
   * at every creation boundary. Immutable after creation: not accepted by
   * `UpdateBankAccountRequest`, since changing it after transactions exist would
   * silently reinterpret their historical amounts.
   */
  currencyCode: string;
  // PIX keys
  pixKeyEvp?: string | null;
  pixKeyCnpj?: string | null;
  pixKeyEmail?: string | null;
  pixKeyPhone?: string | null;
  // CNAB config
  cnabConvenio?: string | null;
  cnabCarteira?: string | null;
  cnabVariacao?: string | null;
  paymentsEnabled?: boolean;
  // Status
  status?: BankAccountStatus;
  isDefault?: boolean;
  /** GL account ID for offset posting (optional). */
  glOffsetAccountId?: string | null;
  /** ALC-4325: "O saldo do extrato inclui o valor aplicado automaticamente". Set by a person only. */
  statementBalanceIncludesSweep?: boolean;
  /**
   * BTG's `accountId` of the account being registered from the BTG integration card
   * (`{cnpj}-208-{branch}-{number}`). Links the new account to it; while the entity's BTG
   * connection is ACTIVE the account is set up to send through BTG (payments stay off).
   */
  btgAccountId?: string;
}

/**
 * Request payload for updating a bank account
 */
export interface UpdateBankAccountRequest {
  statementFileOnly?: boolean;
  name?: string;
  description?: string | null;
  ownerName?: string | null;
  /** null clears the field; bank details are optional for investment and legacy accounts. */
  bankCode?: string | null;
  bankName?: string;
  /** Institution logo URL from Open Finance (Pluggy connector.imageUrl). Optional. */
  bankIconUrl?: string | null;
  branchCode?: string | null;
  accountNumber?: string | null;
  accountType?: BankAccountType;
  // PIX keys
  pixKeyEvp?: string | null;
  pixKeyCnpj?: string | null;
  pixKeyEmail?: string | null;
  pixKeyPhone?: string | null;
  // CNAB config
  cnabConvenio?: string | null;
  cnabCarteira?: string | null;
  cnabVariacao?: string | null;
  paymentsEnabled?: boolean;
  // Status
  status?: BankAccountStatus;
  isDefault?: boolean;
  /** GL account ID for offset posting (optional). */
  glOffsetAccountId?: string | null;
  /** ALC-4325: "O saldo do extrato inclui o valor aplicado automaticamente". Set by a person only. */
  statementBalanceIncludesSweep?: boolean;
  /** User-defined initial balance (editable). Used when no import/Open Finance. */
  initialBalance?: number | string | null;
  /** Date of the initial balance (editable). */
  initialBalanceDate?: Date | string | null;
}

/**
 * One payment type of one gateway on an origin account (ADR 0033): the account's own state and
 * what the gateway's channel has proven. Present on the account detail.
 */
export interface BankAccountPaymentCapability {
  providerId: string;
  instruction: PaymentInstructionKind;
  status: PaymentCapabilityStatus;
  /** The bank's or the operator's explanation for a refused or suspended type; null otherwise. */
  blockedReason: string | null;
  /**
   * Whether a send of this type through this gateway is accepted now, decided by the same
   * predicate the send applies (`isPaymentCapabilityReleased`, the channel and, for an API
   * gateway, its connection); the screen never derives it.
   */
  released: boolean;
  /** What production has proven about this type on the gateway's channel. */
  channel: PaymentChannelCapabilityStatus;
  /** Released while its channel is not confirmed: the first use of this type on the channel. */
  firstUse: boolean;
}

/**
 * Bank account response DTO
 */
export interface BankAccountResponse {
  statementFileOnly: boolean;
  tenantId: string;
  bankAccountId: string;
  entityId: string;
  name: string;
  description: string | null;
  ownerName: string | null;
  bankCode: string | null;
  bankName: string;
  /** Institution logo URL from Open Finance (Pluggy connector.imageUrl). Optional. */
  bankIconUrl: string | null;
  branchCode: string | null;
  accountNumber: string | null;
  accountType: BankAccountType;
  /** Whether this account inherits normal RBAC visibility or requires an FGA grant. */
  accessMode: ResourceAccessMode;
  /** The account's own currency (ALC-1070/1071). Immutable after creation. */
  currencyCode: string;
  // PIX keys
  pixKeyEvp: string | null;
  pixKeyCnpj: string | null;
  pixKeyEmail: string | null;
  pixKeyPhone: string | null;
  // CNAB config
  cnabConvenio: string | null;
  cnabCarteira: string | null;
  cnabVariacao: string | null;
  paymentsEnabled: boolean;
  paymentSetupStatus: 'DISABLED' | 'CONFIGURING' | 'READY' | 'ERROR';
  paymentSetupProvider: string | null;
  /**
   * Gateway kept beside the account default. Enviar still uses `paymentSetupProvider`.
   * The armed gateway is an explicit action, and only after the API refused before accepting.
   */
  paymentVanProvider?: string | null;
  /**
   * One row per gateway and payment type on this account. Present on the account detail.
   * The list endpoint does not load it.
   */
  paymentCapabilities?: BankAccountPaymentCapability[];
  paymentSetupError: string | null;
  lastKnownBalance: number | null;
  balanceUpdatedAt: Date | null;
  initialBalance: number | null;
  initialBalanceDate: Date | null;
  /**
   * ALC-4325: "O saldo do extrato inclui o valor aplicado automaticamente" (sweep account).
   * NOT NULL, default false; set by a person only (backend m3, round 2).
   */
  statementBalanceIncludesSweep: boolean;
  /**
   * Latest external balance read from a provider (Pluggy/Kamino). Optional and
   * populated only by `findOne` to power the "Último Saldo" card and divergence alerts.
   */
  latestSnapshot?: {
    amount: number;
    source: string;
    readAt: Date;
    balanceDate: Date;
  } | null;
  /**
   * Balance the UI should display, picked from the freshest source available
   * (snapshot if newer than `lastKnownBalance`, then `lastKnownBalance`, then a
   * fallback derived from BankStatement/BankAccountDailyBalance). Optional for
   * compatibility with list endpoints that do not compute it.
   */
  effectiveBalance?: {
    amount: number;
    source: 'SNAPSHOT' | 'LAST_KNOWN' | 'COMPUTED';
    updatedAt: Date | null;
    snapshotSource?: string;
  } | null;
  /**
   * Set when the latest snapshot diverges from `lastKnownBalance` beyond the
   * comparison tolerance. Frontend uses it to render an alert prompting the
   * user to investigate / rebase from the snapshot.
   */
  balanceDivergence?: {
    snapshotAmount: number;
    lastKnownAmount: number;
    diff: number;
    percent: number | null;
  } | null;
  // Status
  status: BankAccountStatus;
  isDefault: boolean;
  /** GL account ID for offset posting (optional). */
  glOffsetAccountId: string | null;
  /** Connection ID from Open Finance (Pluggy) when account is linked via aggregator */
  connectionId?: string | null;
  /** Provider account ID from Open Finance when account is linked */
  providerAccountId?: string | null;
  /**
   * Active provider/integration capabilities currently linked to this account
   * (e.g. Open Finance/Pluggy, Kamino). Used by the UI to label the account
   * origin and disambiguate aggregator vs ERP API integrations.
   */
  integrationCapabilities?: {
    capability: string;
    integrationId: string;
    connectionId: string;
  }[];
  /** Last statement import timestamp (from StatementImport.importedAt) */
  lastStatementImportAt?: Date | null;
  /** Last statement import status (PENDING, PROCESSING, COMPLETED, FAILED, CANCELLED) */
  lastStatementImportStatus?:
    'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'CANCELLED' | null;
  /** Error message from last failed import */
  lastStatementImportError?: string | null;
  /** Tecnospeed payment integration config for this bank account, if registered */
  tecnospeedAccountConfig?: {
    accountHash: string | null;
    accountConfigId: string;
  } | null;
  // Audit
  createdByUserId: string | null;
  createdAt: Date;
  updatedAt: Date | null;
}

/** Counts of data still linked to a bank account (soft-delete impact preview). */
export interface BankAccountDeletionImpactResponse {
  originApPaymentCount: number;
  creditCardPaymentLinkCount: number;
  bankTransactionCount: number;
  bankStatementCount: number;
  activeProviderCount: number;
  kaminoStagingCount: number;
  cnabFileCount: number;
  investmentPositionCount: number;
}

// ============================================================================
// Credit Card Types
// ============================================================================

/**
 * Request payload for creating a new credit card
 */
export interface CreateCreditCardRequest {
  entityId: string;
  name: string;
  description?: string | null;
  brand: CreditCardBrand;
  lastFourDigits: string;
  cardholderName: string;
  cardholderEmail?: string | null;
  holderEmployeeId?: string | null;
  expirationMonth: number;
  expirationYear: number;
  creditLimit?: number | null;
  availableLimit?: number | null;
  /** Billed currency; required for new manually-created cards. */
  currencyCode: string;
  closingDay: number;
  dueDay: number;
  /**
   * Optional at the API level (legacy/CSV/programmatic compatibility, and
   * Clara/Conta Simples cards that must never set an institution). Must be
   * set together with financialInstitutionId, or left unset together with
   * it.
   */
  paymentBankAccountId?: string | null;
  status?: CreditCardStatus;
  isDefault?: boolean;
  /** GL account ID for offset posting (optional). */
  glOffsetAccountId?: string | null;
  /** Default AP cost center for this card (optional; must belong to the same entity). */
  costCenterId?: string | null;
  /** Default AP department for this card (optional; must belong to the same entity). */
  departmentId?: string | null;
  /** Issuing financial institution (COMPE registry); see paymentBankAccountId. */
  financialInstitutionId?: string | null;
}

/**
 * Request payload for updating a credit card
 */
export interface UpdateCreditCardRequest {
  name?: string;
  description?: string | null;
  brand?: CreditCardBrand;
  lastFourDigits?: string;
  cardholderName?: string;
  cardholderEmail?: string | null;
  holderEmployeeId?: string | null;
  expirationMonth?: number;
  expirationYear?: number;
  creditLimit?: number | null;
  availableLimit?: number | null;
  closingDay?: number;
  dueDay?: number;
  paymentBankAccountId?: string | null;
  status?: CreditCardStatus;
  isDefault?: boolean;
  /** GL account ID for offset posting (optional). */
  glOffsetAccountId?: string | null;
  costCenterId?: string | null;
  departmentId?: string | null;
  financialInstitutionId?: string | null;
  /**
   * Optional audited currency correction applied in the same command as ordinary
   * field updates. Standalone corrections still use PATCH .../currency.
   */
  currencyCorrection?: UpdateCreditCardCurrencyRequest;
}

/** Audited, effective-dated correction of a credit card's billed currency. */
export interface UpdateCreditCardCurrencyRequest {
  currencyCode: string;
  effectiveAt: string;
  reason: string;
  /** Required when the credit card has more than one provider account. */
  cardAccountId?: string;
}

/**
 * Credit card response DTO
 */
export interface CreditCardResponse {
  tenantId: string;
  creditCardId: string;
  entityId: string;
  name: string;
  description: string | null;
  brand: CreditCardBrand;
  lastFourDigits: string;
  cardholderName: string;
  cardholderEmail: string | null;
  holderEmployeeId: string | null;
  expirationMonth: number;
  expirationYear: number;
  creditLimit: number | null;
  availableLimit: number | null;
  /** Billed currency; null only for legacy cards created before currency was required. */
  currencyCode?: string | null;
  closingDay: number;
  dueDay: number;
  paymentBankAccountId: string | null;
  status: CreditCardStatus;
  isDefault: boolean;
  /** GL account ID for offset posting (optional). */
  glOffsetAccountId: string | null;
  /** Default AP cost center (optional). */
  costCenterId: string | null;
  /** Default AP department (optional). */
  departmentId: string | null;
  /** Connection ID from Open Finance (Pluggy) when card is linked via aggregator */
  connectionId?: string | null;
  /** Provider account identity used for audited currency corrections. */
  cardAccountId?: string | null;
  /** Number of provider accounts linked to this credit card. */
  providerAccountCount?: number;
  /** Provider account ID from Open Finance when card is linked */
  providerAccountId?: string | null;
  /** Origin of the card registration in ERP. */
  source?: 'MANUAL' | 'CLARA' | 'CONTA_SIMPLES' | 'OPEN_FINANCE';
  /** Child/additional/virtual card finals observed in provider statement items. */
  childCardLast4s?: string[];
  /** Number of distinct card finals observed in provider statement items. */
  childCardCount?: number;
  /** Issuing financial institution (COMPE registry). */
  financialInstitutionId?: string | null;
  financialInstitutionBankCode?: string | null;
  financialInstitutionBankName?: string | null;
  // Audit
  createdByUserId: string | null;
  createdAt: Date;
  updatedAt: Date | null;
}

export interface ContaSimplesSyncStatsResponse {
  fetched: number;
  upserted: number;
  errors: number;
}

export interface ContaSimplesFullSyncRequest {
  entityId: string;
  from?: string;
  to?: string;
}

export interface ContaSimplesFullSyncResponse {
  success: boolean;
  categorias: ContaSimplesSyncStatsResponse;
  cards: ContaSimplesSyncStatsResponse;
  bankTransactions: ContaSimplesSyncStatsResponse;
  cardTransactions: ContaSimplesSyncStatsResponse;
  costCenters: { count: number };
}

/** Per-entity Conta Simples connection (API list/upsert response). */
export interface ContaSimplesEntityConnection {
  entityConfigId: string;
  entityId: string;
  entityName: string | null;
  status: string;
  syncEnabled: boolean;
  syncStartDate: string | null;
  autoCreateBillsFromAttachments: boolean;
  credentialsConfigured: boolean;
  lastSyncAt: string | null;
  lastError: string | null;
  createdAt: string;
}

export interface ContaSimplesEntityConfigPayload {
  syncEnabled?: boolean;
  syncStartDate?: string | null;
  autoCreateBillsFromAttachments?: boolean;
  cardTransactionsRefetchLookbackDays?: number | null;
}

export interface ContaSimplesEntityCredentialsPayload {
  apiKey: string;
  apiSecret: string;
}

export interface ContaSimplesEntityConnectionUpsertRequest {
  config?: ContaSimplesEntityConfigPayload;
  credentials?: ContaSimplesEntityCredentialsPayload;
}

/** Why the last connect/refresh failed. Never a raw provider message. */
export type BrexConnectionErrorCode = 'AUTH_FAILED' | 'UNAVAILABLE' | 'MISSING_SCOPE';

/** `code` of a 400 from the Brex connect/map routes. */
export type BrexApiErrorCode = BrexConnectionErrorCode | 'ENTITY_ALREADY_MAPPED';

/** A Brex legal entity discovered on the connected account, and the Alchemy entity it maps to. */
export interface BrexLegalEntityMapping {
  brexLegalEntityId: string;
  brexLegalEntityName: string | null;
  isDefault: boolean;
  entityId: string | null;
  entityName: string | null;
}

/** The tenant's Brex account connection. Never carries the token. */
export interface BrexConnection {
  status: 'ACTIVE' | 'INACTIVE' | 'ERROR';
  credentialsConfigured: boolean;
  /** Last 4 characters of the stored token, or null when disconnected. */
  tokenHint: string | null;
  lastValidatedAt: string | null;
  /** When the last Brex sync (staging + bridge) finished successfully. */
  lastSyncedAt: string | null;
  /** ISO date (yyyy-mm-dd) the first sync reaches back to; null on connections from before the setting. */
  initialSyncFrom: string | null;
  lastError: BrexConnectionErrorCode | null;
  lastErrorAt: string | null;
  /**
   * Whether the token may send payments (reads the transfers endpoint): `false` when Brex refuses
   * it, `null` when unknown (not connected, or Brex could not be reached). Only the connection
   * read computes it; other responses carry `null`.
   */
  paymentsEnabled: boolean | null;
  legalEntities: BrexLegalEntityMapping[];
}

export type BrexSyncJobState =
  'waiting' | 'active' | 'completed' | 'failed' | 'delayed' | 'unknown';

/** Response of the manual sync trigger. `enqueued: false` means a sync is already running or queued. */
export interface BrexSyncEnqueueResponse {
  jobId: string;
  enqueued: boolean;
}

export type BrexSyncMode = 'incremental' | 'reconcile' | 'full';

export interface BrexSyncJobStatusResponse {
  jobId: string;
  state: BrexSyncJobState;
  mode?: BrexSyncMode;
  failedReason?: string;
  /** Set on a waiting job when another sync of the same tenant is running ahead of it. */
  waitingOn?: { jobId: string; mode: BrexSyncMode };
}

/** The tenant's sync running or queued right now, if any. */
export interface BrexSyncCurrentJobResponse {
  job: BrexSyncJobStatusResponse | null;
}

/** `connection` is null until a token has been connected. */
export interface BrexConnectionResponse {
  connection: BrexConnection | null;
}

/** How far back the first Brex sync reaches, in months. */
export const INITIAL_SYNC_MONTH_OPTIONS = [1, 2, 3, 6, 12] as const;
export const DEFAULT_INITIAL_SYNC_MONTHS = 3;

/** How far back a new Plaid connection imports, in months; Plaid allows up to 730 days. */
export const PLAID_INITIAL_SYNC_MONTH_OPTIONS = [1, 2, 3, 6, 12, 24] as const;
export const PLAID_DEFAULT_INITIAL_SYNC_MONTHS = 3;

export interface BrexConnectionUpsertRequest {
  accessToken: string;
  /** Only honored when the connection has no earlier token; defaults to 3. */
  initialSyncMonths?: (typeof INITIAL_SYNC_MONTH_OPTIONS)[number];
}

/** `entityId: null` clears the mapping. */
export interface BrexLegalEntityMappingRequest {
  entityId: string | null;
}

/** Countries with a Clara public API. */
export type ClaraMarketCountry = 'BR' | 'MX' | 'CO';
/** Settlement currency of the Clara cash product, per market. */
export type ClaraSettlementCurrency = 'BRL' | 'MXN' | 'COP';
/** Tax identifier Clara expects in `X-Tax-Identifier`, per market. */
export type ClaraTaxIdentifierType = 'CNPJ' | 'RFC' | 'NIT';

/**
 * Stable Clara failure codes. Setup codes come from resolving the connection;
 * the last three classify a provider failure (sync or connection test). The web
 * translates each one; never classify by message text.
 */
export type ClaraErrorCode =
  | 'CLARA_ENTITY_NOT_FOUND'
  | 'CLARA_COUNTRY_UNSUPPORTED'
  | 'CLARA_TAX_ID_MISSING'
  | 'CLARA_CONNECTION_NOT_FOUND'
  | 'CLARA_CONNECTION_INACTIVE'
  | 'CLARA_CREDENTIALS_MISSING'
  | 'CLARA_CREDENTIALS_REJECTED'
  | 'CLARA_API_UNAVAILABLE'
  | 'CLARA_SYNC_FAILED';

/** Clara market derived from the entity country (never stored). */
export interface ClaraEntityMarket {
  countryCode: ClaraMarketCountry;
  currencyCode: ClaraSettlementCurrency;
  taxIdentifierType: ClaraTaxIdentifierType;
}

/**
 * One row per tenant entity. `market` is null when Clara does not operate in
 * the entity country: the UI shows "Clara is not available in this country" and
 * disables Connect.
 */
export interface ClaraEntityConnection {
  entityId: string;
  entityName: string;
  countryCode: string;
  market: ClaraEntityMarket | null;
  /** Header-ready primary tax identifier issued by the market country, or null when missing. */
  taxIdentifier: string | null;
  entityConfigId: string | null;
  status: 'ACTIVE' | 'INACTIVE' | 'ERROR' | null;
  credentialsConfigured: boolean;
  syncEnabled: boolean;
  autoCreateBillsFromAttachments: boolean;
  enrichAttachmentsWithClaraExtractedDocuments: boolean;
  lastSyncAt: string | null;
  /** Raw provider detail of the last failed sync (technical, untranslated). */
  lastError: string | null;
  /** Stable code of the last failed sync; the UI shows its translation. */
  lastErrorCode: ClaraErrorCode | null;
}

export interface ClaraEntityConfigPayload {
  syncEnabled?: boolean;
  autoCreateBillsFromAttachments?: boolean;
  enrichAttachmentsWithClaraExtractedDocuments?: boolean;
}

export interface ClaraEntityCredentialsPayload {
  clientId: string;
  clientSecret: string;
  clientCertBase64: string;
  clientKeyBase64: string;
}

export interface ClaraEntityConnectionUpsertRequest {
  config?: ClaraEntityConfigPayload;
  credentials?: ClaraEntityCredentialsPayload;
}

// ============================================================================
// Query Types
// ============================================================================

/**
 * Query parameters for finding payment sources
 */
export interface FindPaymentSourcesQuery {
  entityId?: string;
  status?: BankAccountStatus | CreditCardStatus;
  /** Exact bank-account type filter, applied before pagination. */
  accountType?: BankAccountType;
  brand?: CreditCardBrand;
  source?: NonNullable<CreditCardResponse['source']>;
  isDefault?: boolean;
  search?: string;
  page?: number;
  limit?: number;
  /**
   * When true (bank accounts list only), return accounts that have at least one `BankTransaction`,
   * plus any ids listed in `includeBankAccountIds`.
   */
  hasBankTransactions?: boolean;
  /** Comma-separated bank account UUIDs always included when `hasBankTransactions` is true. */
  includeBankAccountIds?: string;
  /** Comma-separated `BankAccountType` values excluded from the list (e.g. `INVESTMENT`). */
  excludeAccountTypes?: string;
  /** Comma-separated AP department UUIDs for manager scope filtering (credit cards). */
  scopeDepartmentIds?: string;
  /** Comma-separated AP cost center UUIDs for manager scope filtering (credit cards). */
  scopeCostCenterIds?: string;
}

/**
 * Query parameters for financial institution lookup selectors.
 */
export interface FindFinancialInstitutionsQuery {
  search?: string;
  isActive?: boolean;
  limit?: number;
}

/**
 * Financial institution response used by selector/search endpoints.
 */
export interface FinancialInstitutionLookupResponse {
  institutionId: string;
  bankCode: string;
  bankName: string;
  officialName: string | null;
  ispb: string | null;
  cnpj: string | null;
  website: string | null;
  isActive: boolean;
}

/** The five deterministic classification slots OnFly travel bills resolve through. */
export const ONFLY_TRAVEL_ACCOUNT_KEYS: readonly OnflyTravelAccountKey[] = [
  'FLIGHT',
  'HOTEL',
  'RENTAL_CAR',
  'BUS',
  'CHANGE_FEE',
];

/** Per-entity OnFly connection (API list/upsert response). */
export interface OnflyEntityConnection {
  entityConfigId: string;
  entityId: string;
  entityName: string | null;
  status: string;
  syncEnabled: boolean;
  /** Waives human review of every OnFly bill. Changed only through the auto-approval route. */
  autoApproveBills: boolean;
  /** The entity's functional currency, resolved at setup — never chosen by the operator. */
  currencyCode: string;
  defaultCostCenterId: string | null;
  credentialsConfigured: boolean;
  lastSyncAt: string | null;
  lastError: string | null;
  createdAt: string;
  /**
   * A live vendor coded `ONFLY` whose CNPJ is not OnFly's. Nothing was written to it; the
   * operator is told so the vendor master can be tidied up.
   */
  conflictingVendorId: string | null;
  /** The vendor OnFly travel bills are raised against. */
  vendorId: string;
}

export interface OnflyEntityConfigPayload {
  syncEnabled?: boolean;
  defaultCostCenterId?: string | null;
}

export interface OnflyEntityCredentialsPayload {
  clientId: string;
  clientSecret: string;
}

export interface OnflyEntityConnectionUpsertRequest {
  config?: OnflyEntityConfigPayload;
  credentials?: OnflyEntityCredentialsPayload;
}

/** One travel-type row in the OnFly account mapping screen. */
export interface OnflyTravelAccountMappingRow {
  accountKey: OnflyTravelAccountKey;
  glAccountId: string | null;
  glAccountCode: string | null;
  glAccountName: string | null;
  managerialAccountId: string | null;
  managerialAccountCode: string | null;
  managerialAccountName: string | null;
}

export interface OnflyTravelAccountMappingResponse {
  entityId: string;
  /**
   * The CoA version these mappings are stored against — resolved through the same entity-anchored
   * accounting scope the classifier reads with, so a saved mapping is the one classification finds.
   */
  coaVersionId: string;
  coaVersionName: string | null;
  rows: OnflyTravelAccountMappingRow[];
}

export interface OnflyTravelAccountMappingUpsertRequest {
  /**
   * The date whose chart of accounts these mappings belong to. Omitted means today. Supplied when
   * mapping a chart that a cutover has superseded, so historical bookings can still be classified.
   */
  accountingDate?: string;
  mappings: {
    accountKey: OnflyTravelAccountKey;
    glAccountId: string;
    managerialAccountId: string;
  }[];
}
